import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { AudienceField } from "@/components/AudienceField";
import { ROLES } from "@/lib/constants";
import { asList } from "@/lib/forms";

const EMPTY = { title: "", description: "", category: "", applicable_roles: [], linked_sop_ids: [], linked_course_ids: [], audience_region_ids: [] };

const CheckList = ({ items, value, onChange, testPrefix }) => (
  <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-lg border border-lasso-line p-3">
    {items.map((it) => (
      <label key={it.id} className="flex cursor-pointer items-center gap-2 text-sm">
        <Checkbox checked={value.includes(it.id)} onCheckedChange={(c) => onChange(c ? [...value, it.id] : value.filter((x) => x !== it.id))} data-testid={`${testPrefix}-${it.id}`} />
        {it.title}
      </label>
    ))}
    {!items.length && <p className="text-xs text-muted-foreground">Nothing published yet.</p>}
  </div>
);

export const FormEditorDialog = ({ open, form, categories, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sops, setSops] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    if (!open) return;
    setF(form ? { title: form.title, description: form.description, category: form.category, applicable_roles: form.applicable_roles, linked_sop_ids: form.linked_sop_ids, linked_course_ids: form.linked_course_ids, audience_region_ids: form.audience_region_ids || [] } : EMPTY);
    setFile(null);
    api.get("/sops").then((r) => setSops(asList(r.data).filter((s) => s.status === "published")));
    api.get("/courses").then((r) => setCourses(asList(r.data).filter((c) => c.status === "published")));
  }, [open, form]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!form && !file) return toast.error("Choose a PDF, DOCX or XLSX file");
    setSaving(true);
    try {
      let data;
      if (form) {
        ({ data } = await api.put(`/forms/${form.id}`, f));
      } else {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/forms/upload", fd);
        ({ data } = await api.post("/forms", { ...f, file_id: up.data.file_id }));
      }
      toast.success(form ? "Form updated" : "Form published");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="form-editor-dialog">
        <DialogHeader><DialogTitle className="font-display">{form ? `Edit ${form.title}` : "New form"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="form-editor-title" /></div>
          <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="form-editor-description" /></div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={f.category || undefined} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="form-editor-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Applicable roles</Label>
            <AudienceField value={f.audience_region_ids} onChange={(v) => setF((x) => ({ ...x, audience_region_ids: v }))} testId="form-editor-audience" />
            <div className="flex flex-wrap gap-4">
              {ROLES.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={f.applicable_roles.includes(r.value)} onCheckedChange={(c) => set("applicable_roles", c ? [...f.applicable_roles, r.value] : f.applicable_roles.filter((x) => x !== r.value))} data-testid={`form-editor-role-${r.value}`} />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label>Linked SOPs</Label><CheckList items={sops} value={f.linked_sop_ids} onChange={(v) => set("linked_sop_ids", v)} testPrefix="form-editor-sop" /></div>
            <div className="space-y-2"><Label>Linked courses</Label><CheckList items={courses} value={f.linked_course_ids} onChange={(v) => set("linked_course_ids", v)} testPrefix="form-editor-course" /></div>
          </div>
          {!form && (
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-lasso-line p-4 text-sm hover:border-lasso-ink">
              <Upload className="h-4 w-4 text-lasso-deep" /> {file ? file.name : "Choose the blank form file (PDF, DOCX or XLSX)"}
              <input type="file" accept=".pdf,.docx,.xlsx" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="form-editor-file" />
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold" data-testid="form-editor-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="form-editor-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {form ? "Save changes" : "Publish form"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
